/**
 * DOM Utility Module
 * 
 * Helper functions for creating and manipulating DOM elements
 * without a framework like React.
 * 
 * @package PresszoneComments
 */

/**
 * Create a DOM element with attributes and children.
 * 
 * @param {string} tag - HTML tag name
 * @param {object} attrs - Attributes and event handlers
 * @param {...(string|Node|Array)} children - Child elements or text
 * @returns {HTMLElement}
 * 
 * @example
 * el('div', { class: 'card', onclick: handler },
 *   el('h2', {}, 'Title'),
 *   el('p', {}, 'Content')
 * )
 */
export function el(tag, attrs = {}, ...children) {
    const element = document.createElement(tag);

    // Set attributes
    for (const [key, value] of Object.entries(attrs)) {
        if (key.startsWith('on') && typeof value === 'function') {
            // Event handler
            const eventName = key.slice(2).toLowerCase();
            element.addEventListener(eventName, value);
        } else if (key === 'class' || key === 'className') {
            element.className = value;
        } else if (key === 'style' && typeof value === 'object') {
            Object.assign(element.style, value);
        } else if (key === 'html') {
            element.innerHTML = value;
        } else if (value !== null && value !== undefined && value !== false) {
            element.setAttribute(key, value);
        }
    }

    // Append children
    for (const child of children.flat()) {
        if (child === null || child === undefined || child === false) {
            continue;
        }
        if (typeof child === 'string' || typeof child === 'number') {
            element.appendChild(document.createTextNode(child));
        } else if (child instanceof Node) {
            element.appendChild(child);
        }
    }

    return element;
}

/**
 * Query selector shortcut.
 * @param {string} selector
 * @param {Element} parent
 * @returns {Element|null}
 */
export function qs(selector, parent = document) {
    return parent.querySelector(selector);
}

/**
 * Query selector all shortcut.
 * @param {string} selector
 * @param {Element} parent
 * @returns {NodeListOf<Element>}
 */
export function qsa(selector, parent = document) {
    return parent.querySelectorAll(selector);
}

/**
 * Clear all children from an element.
 * @param {Element} element
 */
export function clear(element) {
    element.innerHTML = '';
}

/**
 * Show a loading spinner in an element.
 * @param {Element} container
 * @param {string} message
 */
export function showLoading(container, message = null) {
    clear(container);
    
    // Playful loading messages
    const messages = [
        __('Dusting off the comments...', 'comments-press-zone'),
        __('Summoning comments...', 'comments-press-zone'),
        __('Polishing the pixels...', 'comments-press-zone'),
        __('Brewing some fresh data...', 'comments-press-zone'),
        __('Connecting to the hive mind...', 'comments-press-zone')
    ];

    const initialMessage = message || messages[0];

    const messageEl = el('p', { class: 'presszone-comments-loading-text' }, initialMessage);
    
    container.appendChild(
        el('div', { class: 'presszone-comments-loading' },
            el('div', { class: 'presszone-comments-spinner' }),
            messageEl
        )
    );

    // Cycle messages if using default
    if (!message) {
        let index = 0;
        const interval = setInterval(() => {
            if (!container.isConnected) {
                clearInterval(interval);
                return;
            }
            index = (index + 1) % messages.length;
            messageEl.textContent = messages[index];
            // Add a subtle fade effect if we had a way to manipulate classes easily here, 
            // but textContent swap is fine for now.
        }, 2000);
    }
}

/**
 * Get translation string.
 * Falls back to the string itself if not translated.
 * @param {string} text
 * @param {string} domain
 * @returns {string}
 */
export function __(text, domain = 'comments-press-zone') {
    // Use WordPress i18n if available
    if (window.wp?.i18n?.__) {
        return window.wp.i18n.__(text, domain);
    }
    return text;
}

/**
 * Format a date string for display.
 * @param {string} dateStr
 * @returns {string}
 */
export function formatDate(dateStr) {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Debounce a function.
 * @param {Function} fn
 * @param {number} delay
 * @returns {Function}
 */
export function debounce(fn, delay = 300) {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

/**
 * Mount an element to a parent.
 * @param {Element} parent
 * @param {Element} child
 */
export function mount(parent, child) {
    if (parent && child) {
        parent.appendChild(child);
    }
}

export default { el, qs, qsa, clear, showLoading, __, formatDate, debounce, mount };
